import { Stack } from '@superdispatch/ui';
import { Box, Button } from '@superdispatch/ui-lab';
import { useState } from 'react';
import { SuperPayBankAccountDTO } from 'shared/modules/superpay/SuperPayDTO';
import {
  ICantFindMvCodeLink,
  InstantBankVerificationFormContent,
  useInstantBankVerificationStep,
} from '../../../profile/superpay/account-details/InstantBankVerificationForm';
import { useBankAccountVerificationAnalytics } from '../../../profile/data/SuperPayAnalytics';
import { MobileDrawerLayout } from './MobileDrawerLayout';
import { UploadScreenshotStep } from './UploadScreenshotStep';

type Step = 'code' | 'upload_screenshot';

interface BankAccountVerificationWebViewStepProps {
  bankAccount?: SuperPayBankAccountDTO;
  onComplete: () => void;
  isTitleOnTop?: boolean;
}

export function BankAccountVerificationWebViewStep({
  bankAccount,
  onComplete,
  isTitleOnTop = false,
}: BankAccountVerificationWebViewStepProps) {
  const [step, setStep] = useState<Step>('code');
  const { trackVerified, trackCantFindMvCode } =
    useBankAccountVerificationAnalytics();

  if (step === 'upload_screenshot') {
    return (
      <UploadScreenshotStep
        onBack={() => setStep('code')}
        onComplete={onComplete}
        onVerified={trackVerified}
      />
    );
  }

  return (
    <CodeStep
      bankAccount={bankAccount}
      onComplete={onComplete}
      isTitleOnTop={isTitleOnTop}
      onSwitchToScreenshot={() => {
        trackCantFindMvCode();
        setStep('upload_screenshot');
      }}
    />
  );
}

interface CodeStepProps extends BankAccountVerificationWebViewStepProps {
  onSwitchToScreenshot: () => void;
}

function CodeStep({
  bankAccount,
  onComplete,
  isTitleOnTop,
  onSwitchToScreenshot,
}: CodeStepProps) {
  const { trackVerified } = useBankAccountVerificationAnalytics();
  const { formik, isLocked, isSubmitDisabled } = useInstantBankVerificationStep(
    {
      bankAccount,
      onComplete: () => {
        trackVerified();
        onComplete();
      },
      onLocked: onComplete,
    },
  );

  return (
    <MobileDrawerLayout
      formik={formik}
      title="Verify Bank Account"
      action={
        !!bankAccount && (
          <Stack space="small" align="center">
            <Button
              onClick={() => {
                void formik.submitForm();
              }}
              pending={formik.isSubmitting}
              disabled={isSubmitDisabled}
              fullWidth={true}
            >
              Verify
            </Button>
            <ICantFindMvCodeLink
              isLocked={isLocked}
              onClick={onSwitchToScreenshot}
            />
          </Stack>
        )
      }
    >
      <Box marginTop={isTitleOnTop ? undefined : '-small'}>
        <InstantBankVerificationFormContent
          bankAccount={bankAccount}
          titleOnTop={isTitleOnTop}
        />
      </Box>
    </MobileDrawerLayout>
  );
}
